<template>
<div style="text-align:left;">
  <TopNav />
  <div class="column-left">
    <SideNav />
  </div>

  <div class="column-right">
    <div v-if="admin_datos.superadmin" class="main-container">
      <div class="articulo-principal">
        <!-- Welcome message removed -->

        <!-- Section for Pending Amounts per Empresa -->
        <div class="pending-amounts-section">
          <h3>Montos Pendientes por Empresa de Administración (Con Deuda > 0)</h3>
          <div v-if="isLoadingPendingAmounts" class="loading-message">
            Cargando montos pendientes...
          </div>
          <div v-else-if="errorPendingAmounts" class="error-message">
            <p>Error al cargar los datos: {{ errorPendingAmounts }}</p>
          </div>
          <div v-else-if="empresasConDeuda && empresasConDeuda.length > 0">
            <table class="main-table">
              <thead>
                <tr>
                  <th style="width: 5%;"></th> <!-- For expand/collapse icon -->
                  <th>ID Empresa</th>
                  <th>Nombre Empresa</th>
                  <th>Email</th>
                  <th>Total Pendiente</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="empresa in empresasConDeuda">
                  <tr :key="empresa.empresa_id" @click="toggleEmpresaDetails(empresa)" class="expandable-row"
                      :class="{ 'expanded': selectedEmpresaId === empresa.empresa_id }">
                    <td class="expand-icon-cell">
                      <span v-if="selectedEmpresaId === empresa.empresa_id">▼</span> <!-- Down arrow -->
                      <span v-else>▶</span> <!-- Right arrow -->
                    </td>
                    <td>{{ empresa.empresa_id }}</td>
                    <td>{{ empresa.empresa_nombre }}</td>
                    <td>{{ empresa.email }}</td>
                    <td>{{ formatCurrency(empresa.total_pendiente) }}</td>
                  </tr>
                  <!-- Detail Row -->
                  <tr v-if="selectedEmpresaId === empresa.empresa_id" :key="`detail-${empresa.empresa_id}`" class="detail-row">
                    <td colspan="5"> <!-- Colspan to span all columns of the main table -->
                      <div class="detail-content">
                        <div v-if="isLoadingDetail" class="loading-message small-loading">
                          Cargando detalle de solicitudes...
                        </div>
                        <div v-else-if="errorDetail" class="error-message small-error">
                          <p>Error al cargar el detalle: {{ errorDetail }}</p>
                        </div>
                        <div v-else-if="empresaSolicitudesDetail && empresaSolicitudesDetail.length > 0">
                          <h4>Detalle para: {{ empresa.empresa_nombre }}</h4>
                          <table class="detail-table">
                            <thead>
                              <tr>
                                <th>ID Solicitud</th>
                                <th>Dirección</th>
                                <th>Comuna</th>
                                <th>Monto Pendiente</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="solicitud in empresaSolicitudesDetail" :key="solicitud.solicitud_id">
                                <td>{{ solicitud.solicitud_id }}</td>
                                <td>{{ solicitud.direccion }}</td>
                                <td>{{ solicitud.comuna }}</td>
                                <td>{{ formatCurrency(solicitud.monto_pendiente) }}</td>
                              </tr>
                            </tbody>
                          </table>
                           <p style="text-align:right; font-weight:bold; margin-top:10px;">
                              Total Verificado: {{ formatCurrency(empresa.total_pendiente) }}
                            </p>
                        </div>
                        <div v-else class="no-data-message small-no-data">
                          <p>No se encontraron solicitudes con pagos pendientes para esta empresa o ya fueron saldadas.</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div v-else class="no-data-message">
            <p>No hay empresas con montos pendientes mayores a cero.</p>
          </div>
        </div>
      </div>
      <!-- Removed the v-else for pageContentLoaded as we removed the initial welcome message -->
    </div>
  </div>
</div>
</template>

<script>
import axios from 'axios';
import {mapGetters, mapActions} from 'vuex';
import SideNav from '@/components/SideNav.vue';
import TopNav from '@/components/TopNav.vue';

export default {
  name: 'SuperAdminDashboard',
  props:['id'],
  components: {
    SideNav,
    TopNav,
  },
  data() {
    return {
      // pageContentLoaded: true, // No longer needed as welcome message is removed
      
      pendingAmounts: [],
      isLoadingPendingAmounts: true,
      errorPendingAmounts: null,

      // State for detailed view
      selectedEmpresaId: null, // Store only the ID of the selected/expanded empresa
      empresaSolicitudesDetail: [],
      isLoadingDetail: false,
      errorDetail: null,
    }
  },
  computed:{
    ...mapGetters(['comunas', 'categorias', 'regiones', 'tipos', 'admin_datos']),

    empresasConDeuda() {
      if (!this.pendingAmounts) return [];
      return this.pendingAmounts.filter(empresa => empresa.total_pendiente > 0);
    }
  },
  methods:{
    ...mapActions(['initialize']),

    async fetchPendingAmounts() {
      this.isLoadingPendingAmounts = true;
      this.errorPendingAmounts = null;
      this.selectedEmpresaId = null; // Reset detail view when refetching main list
      this.empresaSolicitudesDetail = [];

      try {
        const apiUrl = `${window.hostname}/api/superadmin/pending_amounts_per_empresa`;
        const response = await axios.get(apiUrl);
        
        if (response.data && response.data.pending_amounts_data) {
          this.pendingAmounts = response.data.pending_amounts_data;
        } else {
          this.pendingAmounts = [];
        }
      } catch (error) {
        console.error("Error fetching pending amounts:", error);
        this.errorPendingAmounts = this.formatErrorMessage(error);
        this.pendingAmounts = [];
      } finally {
        this.isLoadingPendingAmounts = false;
      }
    },

    async toggleEmpresaDetails(empresa) {
      if (this.selectedEmpresaId === empresa.empresa_id) {
        // If clicking the same empresa again, collapse it
        this.selectedEmpresaId = null;
        this.empresaSolicitudesDetail = []; // Clear details
        this.errorDetail = null;
        return;
      }
      
      // If clicking a new empresa, or expanding for the first time
      this.selectedEmpresaId = empresa.empresa_id;
      this.isLoadingDetail = true;
      this.errorDetail = null;
      this.empresaSolicitudesDetail = [];

      try {
        const detailApiUrl = `${window.hostname}/api/superadmin/empresa/${empresa.empresa_id}/pending_solicitudes_detail`;
        const response = await axios.get(detailApiUrl);

        if (response.data && response.data.solicitudes_details) {
          this.empresaSolicitudesDetail = response.data.solicitudes_details;
        } else {
          this.empresaSolicitudesDetail = [];
          console.warn("Solicitud details received but not in expected format:", response.data);
        }
      } catch (error) {
        console.error(`Error fetching details for empresa ${empresa.empresa_id}:`, error);
        this.errorDetail = this.formatErrorMessage(error);
      } finally {
        this.isLoadingDetail = false;
      }
    },

    formatCurrency(value) {
      if (typeof value !== 'number') {
        return value;
      }
      const intValue = Math.floor(value); 
      return "$" + intValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },

    formatErrorMessage(error) {
      if (error.response) {
        return `Error ${error.response.status}: ${error.response.data.error || 'El servidor respondió con un error.'}`;
      } else if (error.request) {
        return 'No se pudo conectar al servidor. Verifique su conexión.';
      } else {
        return `Ocurrió un error: ${error.message}`;
      }
    }
  },
  mounted() {
    if(!this.admin_datos.superadmin) {
      this.$router.push('/');
    } else {
      this.fetchPendingAmounts();
    }
  },
}
</script>

<style scoped>
td, th {
  text-align: left;
  padding: 12px 15px;
  border-bottom: 1px solid #e0e0e0; /* Slightly lighter border */
  vertical-align: middle; /* Align content vertically */
}
th {
  background-color: #f7f9fc;
  font-weight: 600;
  color: #333;
}

/* Main Table Styles */
.main-table {
  width: 100%; /* Use full width of its container */
  border-collapse: collapse;
  margin-top: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  border-radius: 6px; /* Rounded corners for the table */
  overflow: hidden; /* To make border-radius work with rows */
}
.main-table tbody tr:hover { /* Hover effect for non-expanded rows */
  background-color: #f1f5f9;
}

.expandable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.expandable-row.expanded {
  background-color: #e6f7ff; /* Light blue for expanded row */
  border-bottom: 2px solid #add8e6; /* Stronger border for expanded */
}
.expand-icon-cell {
  width: 40px; /* Fixed width for icon */
  text-align: center;
  font-size: 1.2em;
  color: #3498db;
}

/* Detail Row Styles */
.detail-row td {
  padding: 0; /* Remove padding from the cell itself */
  border-bottom: 1px solid #e0e0e0; /* Border for the detail row */
}
.detail-content {
  padding: 20px 25px; /* Padding inside the detail content area */
  background-color: #fdfdfd; /* Slightly off-white for detail content */
  border-left: 4px solid #3498db; /* Blue accent line */
}
.detail-content h4 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #2c3e50;
}

/* Detail Table (inside the expanded row) */
.detail-table {
  width: 100%;
  margin-top: 10px;
  border-collapse: collapse;
}
.detail-table th {
  background-color: #f0f4f8;
  padding: 10px 12px;
}
.detail-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e8edf3; /* Lighter border for detail table rows */
}
.detail-table tbody tr:last-child td {
  border-bottom: none; /* No border for the last row in detail table */
}


.column-right{
  background-color: rgb(242, 247, 251);
  padding-top:80px;
  padding-left:260px;
  width:100%;
  min-height: 100vh;
}
.main-container{
  background-color: white;
  margin-top:10px;
  padding:25px;
  border-radius: 8px; 
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}
.articulo-principal{
  /* Container for all content if needed, otherwise can be removed if .main-container is sufficient */
}
.pending-amounts-section {
  /* No extra border needed if main-table has shadow/border-radius */
}
.pending-amounts-section h3 {
  margin-top: 0;
  color: #1a2b47; /* Darker, more professional color */
  padding-bottom: 10px;
  /* border-bottom: 1px solid #e0e0e0; */ /* Can remove if too much lines */
  margin-bottom: 20px;
  font-size: 1.5em;
}

.loading-message, .error-message, .no-data-message {
  text-align: center;
  padding: 25px;
  color: #555;
}
.error-message p {
  color: #e74c3c;
}
/* Smaller messages for inline details */
.small-loading, .small-error, .small-no-data {
  padding: 15px;
  font-size: 0.9em;
}

hr { /* Kept if you want dividers elsewhere, but not used in this table structure */
  border: 0;
  height: 1px;
  background-image: linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0));
  margin: 30px 0;
}

@media (max-width: 800px) {
 .column-right{
  padding-left:10px;
  padding-right: 10px;
 }
 .main-table th, .main-table td, .detail-table th, .detail-table td {
   padding: 8px 10px; /* Reduce padding on smaller screens */
   font-size: 0.9em; /* Slightly smaller font */
 }
 .pending-amounts-section h3 {
   font-size: 1.3em;
 }
 .expand-icon-cell {
   width: 30px;
 }
 .detail-content {
  padding: 15px;
 }
}
</style>